import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import {
  CollectionKindDiscriminants,
  DatasetListItem,
  Role,
  TaskKind,
  User,
} from "../../app/backendTypes";
import {
  useAppSelector,
  useDatasetMembership,
  useDatasetPendingTask,
} from "../../app/hooks";
import Error from "../common/Error";
import Loading from "../common/Loading";
import UserSpan from "../common/users/UserSpan";
import { useCallback, useContext, useEffect, useState } from "react";
import { Close } from "@mui/icons-material";
import { HttpClientContext, X_REQUEST_ID } from "../../app/client";
import { AxiosError } from "axios";
import PendingAction from "../common/tools/PendingAction";
import Morselbar from "../common/Morselbar";

export default function DatasetPermissions(props: {
  collectionId: string;
  collectionName: string;
  dataset: DatasetListItem;
  user: User;
  collectionKind: CollectionKindDiscriminants;
  allowWriteAccess: boolean;
}) {
  const {
    collectionId,
    collectionName,
    dataset,
    user,
    collectionKind,
    allowWriteAccess,
  } = props;

  const { membership, error, loading, reload } = useDatasetMembership(
    collectionId,
    dataset.id,
    user.employee.id
  );

  const [read, setRead] = useState(false);
  const [write, setWrite] = useState(false);

  useEffect(() => {
    if (membership !== null) {
      setRead(membership.has_read);
      setWrite(membership.has_write);
    }
  }, [membership]);

  const {
    task,
    loading: taskLoading,
    error: taskError,
    reload: taskReload,
  } = useDatasetPendingTask(collectionId, dataset.id, user.employee.id);

  if (loading && task === null) return <Loading />;
  if (error) return <Error error={error} />;
  if (membership === null) return <></>;

  const readDisabled = !membership.can_read || task !== null;
  const writeDisabled = !membership.can_write || task !== null;

  return (
    <>
      {membership.owner !== undefined && membership.owner !== null ? (
        <Typography color="text.secondary">
          Owner for this dataset is{" "}
          <UserSpan user={membership?.owner} variant="body1" />
        </Typography>
      ) : (
        <Typography color="error">No owner for this dataset</Typography>
      )}
      <br />
      <Typography>
        Access for <UserSpan user={user} variant="body1" />
      </Typography>

      <FormControlLabel
        disabled={readDisabled}
        control={
          <Checkbox
            checked={task === null ? read : membership.has_read}
            onChange={(e) => {
              setRead(e.target.checked);
            }}
          />
        }
        label={
          <Typography
            color={
              readDisabled
                ? "text.secondary"
                : membership.has_read
                ? read
                  ? undefined
                  : "error"
                : read
                ? "success.main"
                : undefined
            }
          >
            Read
          </Typography>
        }
      />

      {allowWriteAccess && (
        <FormControlLabel
          disabled={writeDisabled}
          control={
            <Checkbox
              checked={task === null ? write : membership.has_write}
              onChange={(e) => {
                setWrite(e.target.checked);
              }}
            />
          }
          label={
            <Typography
              color={
                writeDisabled
                  ? "text.secondary"
                  : membership.has_write
                  ? write
                    ? undefined
                    : "error"
                  : write
                  ? "success.main"
                  : undefined
              }
            >
              Write
            </Typography>
          }
        />
      )}

      <br />
      {task !== null && (
        <>
          <br />
          <PendingAction
            task={task}
            callback={taskReload}
            onCompleted={reload}
          />
          <br />
        </>
      )}
      <br />

      {collectionKind === "BigQueryMim" && (
        <>
          <Typography variant="body2" color="text.secondary">
            If you are unable to change permissions please wait up to 24 hours,
            otherwise visit the{" "}
            <Link
              target="_blank"
              href="https://jira.visma.com/servicedesk/customer/portal/8"
            >
              support portal
            </Link>
          </Typography>
          <br />
        </>
      )}

      <Apply
        disabled={taskLoading || taskError !== undefined || task !== null}
        read={read}
        write={write}
        initialRead={membership.has_read}
        initialWrite={membership.has_write}
        user={user}
        callback={taskReload}
        collectionId={collectionId}
        collectionName={collectionName}
        datasetId={dataset.id}
      />
    </>
  );
}

function Apply(props: {
  disabled: boolean;
  read: boolean;
  write: boolean;
  initialRead: boolean;
  initialWrite: boolean;
  user: User;
  callback: () => void;
  collectionId: string;
  collectionName: string;
  datasetId: string;
}) {
  const {
    disabled,
    read,
    write,
    initialRead,
    initialWrite,
    user,
    callback,
    collectionId,
    collectionName,
    datasetId,
  } = props;

  const isAdmin = useAppSelector((state) => state.auth.roles).includes(
    Role.Administrator
  );

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [taskNote, setTaskNote] = useState<string>("");
  const [override, setOverride] = useState(false);

  const grantRead = read && !initialRead;
  const grantWrite = write && !initialWrite;
  const revokeRead = !read && initialRead;
  const revokeWrite = !write && initialWrite;

  const client = useContext(HttpClientContext);

  const createDatasetAccessTask = useCallback(() => {
    setLoading(true);
    setError(undefined);

    const note = taskNote || undefined;

    const body: TaskKind = {
      kind: "DatasetAccess",
      inner: {
        collection_id: collectionId,
        collection_name: collectionName,
        dataset_id: datasetId,
        actions: {
          grant_read: grantRead,
          grant_write: grantWrite,
          revoke_read: revokeRead,
          revoke_write: revokeWrite,
        },
        target: user.employee.id,
        note,
      },
    };

    client
      ?.post(`/api/tasks?override_approvals=${override}`, body)
      .then(() => {
        callback();
        setOpen(false);
      })
      .catch((e: AxiosError) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    taskNote,
    collectionId,
    collectionName,
    datasetId,
    grantRead,
    grantWrite,
    revokeRead,
    revokeWrite,
    user.employee.id,
    client,
    override,
    callback,
  ]);

  return (
    <>
      <Button
        disabled={
          (read === initialRead && write === initialWrite) ||
          disabled ||
          loading
        }
        variant="contained"
        onClick={() => setOpen(true)}
      >
        Request Changes
      </Button>

      <Dialog open={open} fullWidth>
        <DialogTitle>
          Request Changes
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            onClick={() => setOpen(false)}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography>
            <UserSpan user={user} /> will be have the following permission
            changes in{" "}
            <Typography component="span" color="secondary" fontWeight="bold">
              {datasetId}
            </Typography>
            <br />
            <br />
            {grantRead && (
              <Typography
                component="span"
                fontWeight="bold"
                color="success.main"
              >
                &bull; grant read
                <br />
              </Typography>
            )}
            {grantWrite && (
              <Typography
                component="span"
                fontWeight="bold"
                color="success.main"
              >
                &bull; grant write
                <br />
              </Typography>
            )}
            {revokeRead && (
              <Typography component="span" fontWeight="bold" color="error">
                &bull; revoke read
                <br />
              </Typography>
            )}
            {revokeWrite && (
              <Typography component="span" fontWeight="bold" color="error">
                &bull; revoke write
                <br />
              </Typography>
            )}
          </Typography>

          <br />

          <TextField
            onChange={(e) => setTaskNote(e.target.value.trim())}
            placeholder="(Optional) Add a note"
            label="Note"
            size="small"
            fullWidth
          />

          <br />
          <br />

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              {isAdmin && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={override}
                        onChange={(e) => setOverride(e.target.checked)}
                      />
                    }
                    label="Skip approvals"
                  />
                </>
              )}
            </Box>

            <Button
              onClick={() => createDatasetAccessTask()}
              disabled={loading}
              variant="outlined"
            >
              Confirm
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to create task"
        error={error}
      />
    </>
  );
}
